<template>
  <div class="v3-widget v3-stats" v-testid="testid">
    <template v-if="showSkeleton">
      <div v-for="n in skeletonCount" :key="'sk' + n" class="v3-stat v3-stat--skel">
        <span class="v3-skel-chip"></span>
        <div class="v3-stat-main v3-skel-grow">
          <div class="v3-skel-line w60"></div>
          <div class="v3-skel-line w40 big"></div>
        </div>
      </div>
    </template>
    <template v-else>
      <!-- A tile whose destination the user can't reach stays visible (the
           count is still valid) but renders as a plain, non-clickable tile —
           legacy landing-config parity. -->
      <component
        :is="tileClickable(t) ? 'button' : 'div'"
        v-for="(t, i) in sr.tiles.value"
        :key="i"
        class="v3-stat"
        :class="{ 'v3-stat--attn': t.attention, 'v3-stat--btn': tileClickable(t) }"
        v-testid="testid + '-row-' + (t.to || t.label)"
        :type="tileClickable(t) ? 'button' : undefined"
        @click="tileClickable(t) ? go(t) : undefined"
      >
        <span class="v3-ic" :class="'v3-ic--' + (t.colour || 'neutral')"><span class="ms">{{ t.icon }}</span></span>
        <div class="v3-stat-main">
          <div class="label">{{ t.label }}</div>
          <div class="value">{{ t.value }}</div>
          <div v-if="t.hint" class="hint">{{ t.hint }}</div>
        </div>
        <span v-if="tileClickable(t)" class="v3-stat-chev"><span class="ms">chevron_right</span></span>
      </component>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { getCurrentInstance } from "vue";
import { mapState } from "vuex";
import { statRow } from "@rm-ui/homepage/statRow";
import { canNavigateTo } from "@/composables/useV3Nav";

// New-portal (v3) StatRow — thin Vue 2 presentation over the shared `statRow`
// composable (@rm-ui/homepage/statRow). Configurable tiles (icon/colour/label/
// statField); the composable fetches report/activity sources + resolves doc
// counts from the docs store. Sibling presentation: rm-portal-beta StatRowWidget.vue.
export default {
  name: "cLandingStatRow",
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props) {
    const store = getCurrentInstance().proxy.$store;
    const sr = statRow({
      http: axios,
      getTiles: () => props.definition.tiles || [],
      getDocTypeCounts: () =>
        (store.state.docs.docTypeSummary || []).map((s) => ({ name: s.tmpl_name || s.type || s.docType, count: s.count })),
      getStatusCounts: () =>
        (store.state.docs.docStatusSummary || []).map((s) => ({ name: s.status || s.lifecycle_status_name, count: s.count })),
    });
    return { sr };
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3StatRow";
    },
    ...mapState({
      docsLoading: (state) => state.docs.loading,
      docTypeSummary: (state) => state.docs.docTypeSummary,
    }),
    skeletonCount() {
      const n = (this.definition.tiles || []).length;
      return n || 4;
    },
    showSkeleton() {
      const noCounts = !(this.docTypeSummary && this.docTypeSummary.length);
      return (this.docsLoading && noCounts) || this.sr.loading.value;
    },
  },
  methods: {
    // Route permission gate (legacy landing-config parity); the editor shows
    // every tile as clickable so admins can verify targets.
    tileClickable(t) {
      if (!t.to) return false;
      return this.editMode || canNavigateTo(this, t.to);
    },
    go(t) {
      if (this.editMode || !t.to) return;
      if (/^https?:\/\//i.test(t.to)) {
        window.open(t.to, "_blank");
        return;
      }
      // Carry a preset filter to the destination (e.g. a "Pending Approval" tile
      // → All Jobs with that status preselected). `$passedParams` is the one-shot
      // hand-off the docs page reads on init (statuses → docStatusIncluded,
      // docTypes → docTypeIncluded).
      const statuses = Array.isArray(t.statuses) ? t.statuses : [];
      const docTypes = Array.isArray(t.docTypes) ? t.docTypes : [];
      if ((statuses.length || docTypes.length) && this.$passedParams) {
        this.$passedParams.set({ statuses, docTypes });
      }
      this.$router.push({ name: t.to }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
